import { useParams, useSearchParams } from 'react-router-dom';
import ApiStatus from "../apiStatus";
import { useFetchDesiredCapabilitiesForUnitAndMission, useUpdateUnitMissionCapability, useDeleteUnitMissionCapability } from "../hooks/UnitMissionCapabilityHooks";
import ValidationSummary from "../ValidationSummary";
import UnitMissionCapabilityForm from "./UnitMissionCapabilityForm";
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { UnitMissionCapabilityForUnitAndMissionDTO } from '../types/unitMissionCapabilityForUnitAndMissionDTO';

const CapabilityEdit = () => {
  const { t } = useTranslation();
  const { organisationId } = useParams<{ organisationId: string }>();
  const [searchParams] = useSearchParams();
  const unitId = searchParams.get('unitId');
  const missionCodeId = searchParams.get('missionCodeId');
  if (!organisationId) throw Error("Need a organisation id");
  if (!unitId) throw Error("Need a unit id");
  if (!missionCodeId) throw Error("Need a mission code id");

  const [capabilityToDelete, setCapabilityToDelete] = useState<UnitMissionCapabilityForUnitAndMissionDTO | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { data, status, isSuccess } = useFetchDesiredCapabilitiesForUnitAndMission(organisationId, unitId, missionCodeId);
  const updateUnitMissionCapabilityMutation = useUpdateUnitMissionCapability(organisationId);
  const deleteUnitMissionCapabilityMutation = useDeleteUnitMissionCapability(organisationId);
  const [unitMissionCapability, setUnitMissionCapability] = useState<UnitMissionCapabilityForUnitAndMissionDTO>();

    useEffect(() => {
        if (data) setUnitMissionCapability(data[0]);        
    }, [data]);

  const handleCapabilityDelete = (capability: UnitMissionCapabilityForUnitAndMissionDTO) => {
    setCapabilityToDelete(capability);
    setShowDeleteModal(true);
  };

  const handleDelete = () => {
    if (!capabilityToDelete) return;
    deleteUnitMissionCapabilityMutation.mutate(capabilityToDelete, {
      onSuccess: () => {
        setShowDeleteModal(false);
        toast.success(t('unitMissionCapability.deleteSuccess'));
        history.back();
      },
      onError: () => {
        setShowDeleteModal(false);
        toast.error(t('unitMissionCapability.deleteError'));
      }
    });
  };

  if (!isSuccess) return <ApiStatus status={status} />;

  return (
    <>
      {updateUnitMissionCapabilityMutation.isError && (
        <ValidationSummary error={updateUnitMissionCapabilityMutation.error} />
      )}
          {updateUnitMissionCapabilityMutation.isPending &&
              <div className="spinner-container" style={{ textAlign: 'center', marginTop: '20px' }}>
                  <ClipLoader size={50} color={"#123abc"} loading={true} />
                  <p>
                      {t('unitMissionCapability.editWait')}
                  </p>
              </div>}

          {unitMissionCapability &&

              <UnitMissionCapabilityForm
                  headerText={t('unitMissionCapability.edit')}
                  isEdit={true}
                  unitMissionCapabilityForUnitAndMission={unitMissionCapability}
                  submitted={(capabilities) => {                  
                      updateUnitMissionCapabilityMutation.mutate(capabilities, {
                          onSuccess: () => {
                              toast.success(t('unitMissionCapability.editSuccess'));
                              history.back();
                          },
                          onError: () => {
                              toast.error(t('unitMissionCapability.editError'));
                          }
                      });
                  }}
                  deleted={handleCapabilityDelete}
              />
          }

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('common.confirmation')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('unitMissionCapability.deleteConfirmation')}</p>
          {capabilityToDelete && (
            <>
              <div><strong>{t('common.unit')}:</strong> {capabilityToDelete.unit?.name}</div>
              <div><strong>{t('common.missionCode')}:</strong> {capabilityToDelete.missionCode ? `${capabilityToDelete.missionCode.codedValue} ${capabilityToDelete.missionCode.value}` : t('common.default')}</div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleDelete}>
            {t('buttons.delete')}
          </Button>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            {t('buttons.cancel')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CapabilityEdit;